import { state, getSelectedKeyframe, jumpToHistory, subscribe, setHistoryChangeHook, setHistoryConfirmHook } from './state.js';
import { showConfirmDialog } from './ui.js';
import { renderAll } from './render-bridge.js';

let panelOpen = false;

export function initHistoryPanel() {
  const toggle = document.getElementById('history-toggle');
  const panel = document.getElementById('history-panel');
  if (!toggle || !panel) return;

  toggle.addEventListener('click', (e) => {
    e.stopPropagation();
    panelOpen = !panelOpen;
    if (panelOpen) {
      panel.hidden = false;
      renderHistoryPanel();
    } else {
      panel.hidden = true;
    }
  });

  document.addEventListener('click', (e) => {
    if (!panelOpen) return;
    if (panel.contains(e.target) || toggle.contains(e.target)) return;
    panelOpen = false;
    panel.hidden = true;
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && panelOpen) {
      panelOpen = false;
      panel.hidden = true;
    }
  });

  // Re-render on any state change so the count badge stays accurate when open
  subscribe(() => { if (panelOpen) renderHistoryPanel(); renderHistoryBadge(); });
  setHistoryChangeHook(() => { if (panelOpen) renderHistoryPanel(); renderHistoryBadge(); });

  setHistoryConfirmHook(({ onConfirm, onCancel }) => {
    showConfirmDialog({
      title: 'Override history?',
      message: 'This will override all the follow-up changes you had. Are you sure you want to start editing from this state in history?',
      okLabel: 'Override',
      cancelLabel: 'Cancel',
      danger: true,
      onConfirm: () => { onConfirm(); renderAll(); },
      onCancel: () => { onCancel(); renderAll(); },
    });
  });

  renderHistoryBadge();
}

function relTime(ts) {
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 5) return 'just now';
  if (s < 60) return s + 's ago';
  const m = Math.round(s / 60);
  if (m < 60) return m + 'm ago';
  const h = Math.round(m / 60);
  return h + 'h ago';
}

function renderHistoryBadge() {
  const toggle = document.getElementById('history-toggle');
  if (!toggle) return;
  const kf = getSelectedKeyframe();
  if (!kf || !kf.history) {
    toggle.textContent = 'History';
    return;
  }
  toggle.textContent = `History ${kf.historyIndex + 1}/${kf.history.length}`;
}

export function renderHistoryPanel() {
  const panel = document.getElementById('history-panel');
  if (!panel) return;
  const kf = getSelectedKeyframe();
  if (!kf || !kf.history) {
    panel.innerHTML = '<div class="history-empty">No keyframe selected.</div>';
    return;
  }

  panel.innerHTML = '';

  const header = document.createElement('div');
  header.className = 'history-panel-header';
  header.innerHTML = `<span class="history-title">History — ${escapeHtml(kf.name)}</span>`;
  panel.appendChild(header);

  const nav = document.createElement('div');
  nav.className = 'history-nav';

  const back = document.createElement('button');
  back.className = 'btn-sm';
  back.textContent = '← Step back';
  back.title = 'Step back in history';
  back.disabled = kf.historyIndex <= 0;
  back.addEventListener('click', () => {
    jumpToHistory(kf, kf.historyIndex - 1);
    renderAll();
  });
  nav.appendChild(back);

  const fwd = document.createElement('button');
  fwd.className = 'btn-sm';
  fwd.textContent = 'Step forward →';
  fwd.title = 'Step forward in history';
  fwd.disabled = kf.historyIndex >= kf.history.length - 1;
  fwd.addEventListener('click', () => {
    jumpToHistory(kf, kf.historyIndex + 1);
    renderAll();
  });
  nav.appendChild(fwd);

  panel.appendChild(nav);

  const list = document.createElement('div');
  list.className = 'history-list';
  for (let i = kf.history.length - 1; i >= 0; i--) {
    const entry = kf.history[i];
    const row = document.createElement('button');
    row.className = 'history-row';
    if (i === kf.historyIndex) row.classList.add('current');
    row.innerHTML = `
      <span class="history-row-idx">#${i + 1}</span>
      <span class="history-row-label">${escapeHtml(entry.label)}</span>
      <span class="history-row-time">${relTime(entry.ts)}</span>
    `;
    row.addEventListener('click', () => {
      if (i === kf.historyIndex) return;
      jumpToHistory(kf, i);
      renderAll();
    });
    list.appendChild(row);
  }
  panel.appendChild(list);
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
}
